
import { Badge } from "@/components/ui/badge";
import { CollectionStats } from "../CollectionCardStats";
import { useProcessingStatus } from "@/hooks/useProcessingStatus";
import { Loader2 } from "lucide-react";
import { useQuery } from '@tanstack/react-query';
import { supabase } from "@/integrations/supabase/client";

interface StatusBadgesProps {
  stats?: CollectionStats;
  collectionId: string;
}

export const StatusBadges = ({ stats, collectionId }: StatusBadgesProps) => {
  const { data: processingStatus, isLoading } = useProcessingStatus(collectionId);

  // Get actual product completion status to prevent showing "Initializing" for completed collections
  const { data: actualProductStatus } = useQuery({
    queryKey: ['product-completion-check', collectionId],
    queryFn: async () => {
      const { data: products } = await supabase
        .from('product_descriptions')
        .select('processing_status, status')
        .eq('collection_id', collectionId);

      if (!products || products.length === 0) return null;

      const totalProducts = products.length;
      const completedProducts = products.filter(p => 
        p.processing_status === 'completed' || p.status === 'approved'
      ).length;

      return {
        total: totalProducts,
        completed: completedProducts,
        isFullyCompleted: completedProducts === totalProducts && totalProducts > 0
      };
    },
    staleTime: 5000, // Cache for 5 seconds to avoid excessive requests
  });
  
  // Only show processing status if it's truly active (not completed or stale)
  const isActiveProcessing = processingStatus && 
                            processingStatus.status === 'processing' && 
                            processingStatus.total_items > 0 &&
                            processingStatus.processed_items < processingStatus.total_items;
  
  if (isActiveProcessing) {
    const totalItems = processingStatus.total_items || 0;
    const processedItems = processingStatus.processed_items || 0;
    const percentage = totalItems > 0 ? Math.round((processedItems / totalItems) * 100) : 0;
    
    let stageLabel = "Processing";
    switch (processingStatus.current_stage) {
      case 'processing':
        if (percentage < 33) {
          stageLabel = "Extracting data";
        } else if (percentage < 66) {
          stageLabel = "Generating content";
        } else {
          stageLabel = "Finalizing";
        }
        break;
      case 'finalizing':
        stageLabel = "Finalizing";
        break;
    }
    
    // Show initialization state only if products are NOT fully completed
    // This prevents showing "Initializing" for collections that are already done
    if (processedItems === 0 && !actualProductStatus?.isFullyCompleted) {
      return (
        <div className="flex gap-2">
          <Badge variant="secondary" className="whitespace-nowrap flex gap-2 items-center">
            <Loader2 className="h-3 w-3 animate-spin" />
            Initializing process...
          </Badge>
        </div>
      );
    }
    
    return (
      <div className="flex gap-2">
        <Badge variant="secondary" className="whitespace-nowrap flex gap-2 items-center">
          <Loader2 className="h-3 w-3 animate-spin" />
          {stageLabel}: {processedItems} of {totalItems} ({percentage}%)
        </Badge>
      </div>
    );
  }

  // Show a loading state only if we're still fetching and don't have stats yet
  if (isLoading && !stats) {
    return (
      <div className="flex gap-2">
        <Badge variant="secondary" className="whitespace-nowrap flex gap-2 items-center">
          <Loader2 className="h-3 w-3 animate-spin" />
          Loading status...
        </Badge>
      </div>
    );
  }

  // Show regular stats when not processing or completed
  return (
    <div className="flex gap-2">
      <Badge variant="secondary" className="whitespace-nowrap">
        {stats?.total_count || 0} products
      </Badge>
      {stats?.pending_count ? (
        <Badge variant="destructive" className="whitespace-nowrap">
          {stats.pending_count} pending
        </Badge>
      ) : stats?.total_count ? (
        <Badge variant="success" className="whitespace-nowrap bg-green-500 hover:bg-green-600">
          Approved
        </Badge>
      ) : null}
    </div>
  );
};
